/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import APM from '../APM';
import {send} from '../beacons/Beacon';
import {IMonitor, MonitorCategory} from '../types/types';
import BaseMonitor from './BaseMonitor';
import getPageMonitor from './PagePerformanceMonitor';

export interface ICrashEventMonitor extends IMonitor {
  onCrash(error?: Error): boolean
}

/**
 * Defines the Crash Event Monitor implementation which is used for tracking
 * application crash events within an Application.
 */
class CrashEventMonitor extends BaseMonitor implements ICrashEventMonitor {
  constructor() {
    super(MonitorCategory.Crash);
  }

  /**
   * The `onCrash` method is to be invoked when a crash event occurs, the method
   * should be provided with a reference to the error which caused the crash (if
   * available). Generally, client's should not need to invoke this method directly
   * as it is automatically integrated within the ErrorBoundary Components for each
   * page.
   *
   * @param error A reference to the error which caused the crash event.
   */
  onCrash(error?: Error): boolean {
    const page = this.context || globalThis?.location.pathname;
    if (getPageMonitor()?.isRunning()) {
      getPageMonitor().stop();
    }
    this._logger.monitorCrashEvent(page, error?.message);
    return send(this._category, page, {error});
  }
}

/**
 * Provides the single point of entry for referencing the `CrashEventMonitor`
 * instance.
 */
const getMonitor = (() => {
  let instance: ICrashEventMonitor;
  return (): ICrashEventMonitor => {
    if (APM.isEnabled) {
      if (!instance) {
        instance = new CrashEventMonitor();
      }
    } else {
      instance = null;
    }
    return instance;
  };
})();

export default getMonitor;
